import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { compose } from 'redux';
import _ from 'lodash';
import { withTranslation } from 'react-i18next';
import injectReducer from '@utils/injectReducer';
import injectSaga from '@utils/injectSaga';

import { ROUTES } from '@shared/constants';
import TimeTracker from '@shared/TimeTracker';
import { V2_MONITOR_REGISTRY } from '@shared/Resources/Monitor/v2Monitors/registry';
import SectionLoading from '@components/SectionLoading';
import {
  getUSADateFromFullDate,
  getUSATimeFromFullDate,
} from '@utils/timeHelpers';
import Text, { TEXT_SIZE, TEXT_WEIGHT, TEXT_COLOR } from '@components/Text';
import { BackButton } from '@components/BackButton';
import { openPopUp } from '../../../App/containers/PopUp/actions';
import POPUP_TYPES from '../../../App/containers/PopUp/types';

import SurveyJsPollRuntime from '@shared/Resources/Poll/Client/SurveyJsPollRuntime';
import AutoFeedbackTextArea from './containers/AutoFeedbackTextArea';
import ExerciseItem from './containers/ExerciseItem';
import FeedbackPost from './containers/FeedbackPost';

import { getUserActivity, postFeedback, getExerciseProductions, deleteFeedback } from './actions';
import { ACTIVITY_TYPES, MONGO_ID_LENGTH } from './constants';
import reducer from './reducer';
import saga from './saga';
import {
  Wrapper,
  MainSection,
  ActivityHeader,
  HeaderTitle,
  StyledDismissButton,
  HeaderSectionTitleWrapper,
  TextWithSpaces,
  PostsSection,
  UserInfoContainer,
  UserInfoText,
  UserAvatar,
  SectionWrapper,
  SubTitleWrapper,
  SliderView,
  StyledArrowRightIcon,
  StyledArrowLeftIcon,
  SliderItem,
  EntityExerciseWrapper,
  EntityExerciseHeader,
  EntityExerciseName,
  ExerciseScoreHeadings,
} from './styles';
import { getStatusText, STATUS_TYPES } from '../PendingDashboard/shared/SharedComponents';
import { getAgeAndEmailText } from '../UserPage/helpers';
import { PracticeErrorsCard } from './containers/practice-errors-card';
import { ActivityType } from '@shared/TimeTracker/types';
import { mentorRoles } from '@shared/constants/roles';

class ActivityPage extends Component {
  componentDidMount() {
    const { activityType, activityId, activity } = this.props.match.params;
    if (!this.validateActivityType(activityType)) return;
    if (!this.validateActivityId(activityId)) return;

    if (!activity || activity.id !== activityId) {
      this.props.dispatchGetUserActivity(activityType, activityId);
    }
  }

  onPostClicked = (text, isMeaningful) => {
    if (!this.props.isClinicalMentor) return;
    this.props.dispatchPostFeedback(this.props.activity, text, false, isMeaningful);
  };

  onBackClicked = () => {
    this.props.history.goBack();
  };

  onDismiss = () => {
    const { dispatchDismissActivity, dispatchOpenPopup, activity } = this.props;
    dispatchOpenPopup(POPUP_TYPES.CONFIRM_DISMISS_ACTIVITY, {
      onConfirm: (reason) => dispatchDismissActivity(activity, reason),
      hideCloseIcon: true,
    });
  };

  onGetProductions = (activityId) => {
    this.props.dispatchGetExerciseProductions(activityId);
  };

  getErrorMessage = () => {
    const { activityType, activityId } = this.props.match.params;
    if (!this.validateActivityType(activityType)) {
      return this.props.t('Informative.Negative.invalidActivityType', { type: activityType });
    }
    if (!this.validateActivityId(activityId)) {
      return this.props.t('Informative.Negative.invalidActivityId');
    }
    return this.props.error;
  };

  getProductionForExercise = ({ id }) => this.props.loadedProductions[id] || { loading: true };

  validateActivityId = (activityId) => activityId.length === MONGO_ID_LENGTH;

  validateActivityType = (activityType) => Boolean(ACTIVITY_TYPES[activityType]);

  headerSectionTitle = (message) => (
    <HeaderSectionTitleWrapper>
      <Text size={TEXT_SIZE.H5} textColor={TEXT_COLOR.DISABLED} weight={TEXT_WEIGHT.BOLD}>
        <span>{message}</span>
      </Text>
    </HeaderSectionTitleWrapper>
  );

  renderPosts = () => {
    const {
      activity: { Threads, type, pollSkipped, summary },
      postsLoading,
      postingFeedback,
      isClinicalMentor,
    } = this.props;
    const posts = _.get(Threads[0], 'Posts', []);
    const pollFeedbackDisabled = type === ACTIVITY_TYPES.ACTIVITY_POLL && pollSkipped;
    const practiceFeedbackDisabled = type === ACTIVITY_TYPES.ACTIVITY_PRACTICE && !summary?.hasPassed;
    if (pollFeedbackDisabled || practiceFeedbackDisabled) {
      return (
        <PostsSection style={{ alignItems: 'center' }}>
          <Text size={TEXT_SIZE.H5} textColor={TEXT_COLOR.DISABLED} weight={TEXT_WEIGHT.BOLD}>
            <span>{this.props.t(`Informative.Negative.${pollFeedbackDisabled ? 'pollFeedbackDisabled' : 'practiceFeedbackDisabled'}`)}</span>
          </Text>
        </PostsSection>
      );
    }
    return (
      <PostsSection>
        {isClinicalMentor && (
          <AutoFeedbackTextArea
            className="feedbackInput"
            disabled={postingFeedback}
            onPostClicked={this.onPostClicked}
            hasError={!!this.props.errorFeedback}
          />
        )}
        {posts.length > 0 && (
          <>
            {this.renderSubTitle('UI.previousFeedback')}
            {posts.map((post) => (
              <FeedbackPost className="feedbackPost" key={`${post.id}`} post={post} postsLoading={postsLoading} />
            ))}
          </>
        )}
      </PostsSection>
    );
  };

  renderError = (errorMsg) => (
    <div>
      <Text className="errorMessage">{errorMsg}</Text>
    </div>
  );

  renderHeader = () => {
    const {
      activity: {
        Threads,
        summary,
        user,
        entity,
        ParentSkill: { name: skillName },
        type: activityType,
        activityCreated,
      },
      t,
    } = this.props;
    const isPollActivity = activityType === ACTIVITY_TYPES.ACTIVITY_POLL;
    const hasPassed = isPollActivity ? true : Boolean(summary?.hasPassed);
    const isSkipped = isPollActivity ? Boolean(this.props.activity?.pollSkipped) : false;
    const statusMessage = t(`Common.Statuses.${isSkipped ? 'skipped' : hasPassed ? 'complete' : 'incomplete'}`);
    let startDate = 'N \\ A';
    let startTime = '';
    let endTime = '';
    let endDate = '';

    if (activityCreated) {
      startDate = getUSADateFromFullDate(activityCreated.at);
      startTime = getUSATimeFromFullDate(activityCreated.at);
    }
    if (summary?.passedAt) {
      endDate = getUSADateFromFullDate(summary.passedAt);
      endTime = getUSATimeFromFullDate(summary.passedAt);
    }
    const textProps = {
      size: TEXT_SIZE.H4,
      textColor: TEXT_COLOR.DARK_BLUE,
    };
    const ageAndEmailText = getAgeAndEmailText(user);
    const posts = _.get(Threads[0], 'Posts', []);
    return (
      <ActivityHeader className="title">
        <HeaderTitle>
          <Text
            size={TEXT_SIZE.TL0}
            textColor={TEXT_COLOR.DARK_BLUE}
            weight={TEXT_WEIGHT.BOLD}
            className="activityName"
          >
            {entity.name}
          </Text>
          <StyledDismissButton className="dismissButton" onClick={this.onDismiss} disabled={posts.length > 0}>
            {t('Actions.Instance.dismiss')}
          </StyledDismissButton>
        </HeaderTitle>
        <div className="parentSkill">
          {this.headerSectionTitle(t('Common.UI.singleSkill').toUpperCase())}
          <Text {...textProps} className="activitySkillName">
            {skillName}
          </Text>
        </div>
        <div>
          {this.headerSectionTitle(t('UI.activityType').toUpperCase())}
          <Text {...textProps} className="activityType">
            {_.capitalize(activityType.replace('ACTIVITY_', ''))}
          </Text>
        </div>
        <div>
          {this.headerSectionTitle(t('Common.UI.status'))}
          {getStatusText(
              <div className="activityStatus">
                {statusMessage}
              </div>,
              isSkipped ? STATUS_TYPES.SKIPPED : hasPassed ? STATUS_TYPES.DONE : STATUS_TYPES.FAILED,
          )}
        </div>
        <div className="activityStartTime">
          {this.headerSectionTitle(t('UI.startTime').toUpperCase())}
          <TextWithSpaces {...textProps} className="startTime">
            {`${startDate} ${startTime}`}
          </TextWithSpaces>
        </div>
        <div className="activityLength">
          {this.headerSectionTitle(t('UI.endTime').toUpperCase())}
          <TextWithSpaces {...textProps} className="endTime">
            {`${endDate} ${endTime}`}
          </TextWithSpaces>
        </div>
        {user && (
          <UserInfoContainer className="user">
            <UserAvatar className="userAvatar" src={user.avatar} />
            <UserInfoText>
              <TextWithSpaces {...textProps} weight={TEXT_WEIGHT.BOLD} className="userName">
                {user.fullName}
              </TextWithSpaces>
              {ageAndEmailText && (
                <Text size={TEXT_SIZE.T5} textColor={TEXT_COLOR.DISABLED} className="userDetails">
                  {ageAndEmailText}
                </Text>
              )}
            </UserInfoText>
          </UserInfoContainer>
        )}
      </ActivityHeader>
    );
  };

  renderExercises = () => {
    const {
      activity: { EntityExercises, exercises },
    } = this.props;

    return (
      <SectionWrapper>
        {this.renderSubTitle('Common.UI.exercise')}
        {EntityExercises.map((entityExercise) => {
          const relatedExercises = exercises.filter((exercise) => exercise.entity.id === entityExercise.id);
          const showsScores = relatedExercises.some(({ entity }) => {
            const monitorDefinition = V2_MONITOR_REGISTRY[entity.monitor?.monitor?.type];
            return monitorDefinition?.resultPolicy?.usesMastery !== false;
          });
          return (
            <EntityExerciseWrapper key={entityExercise.id}>
              <EntityExerciseHeader>
                <EntityExerciseName
                  weight={TEXT_WEIGHT.BOLD}
                  size={TEXT_SIZE.T1}
                  textColor={TEXT_COLOR.DARK_BLUE}
                >
                  {entityExercise.name}
                </EntityExerciseName>
                {showsScores && (
                  <ExerciseScoreHeadings>
                    <Text size={TEXT_SIZE.T4} style={{ width: '30px' }} textColor={TEXT_COLOR.DARK_BLUE}>
                      {this.props.t('Informative.Statistical.score')}
                    </Text>
                    <Text size={TEXT_SIZE.T4} style={{ width: '80px' }} textColor={TEXT_COLOR.DARK_BLUE}>
                      {this.props.t('Informative.Statistical.requiredScore')}
                    </Text>
                  </ExerciseScoreHeadings>
                )}
              </EntityExerciseHeader>
              {relatedExercises.map((exercise) => (
                <ExerciseItem
                  key={exercise.id}
                  exerciseIndex={String(relatedExercises.indexOf(exercise) + 1)}
                  className="exercise"
                  exercise={exercise}
                  onGetProductions={this.onGetProductions}
                  productions={this.getProductionForExercise(exercise)}
                />
              ))}
            </EntityExerciseWrapper>
          );
        })}
      </SectionWrapper>
    );
  };

  renderSubTitle = (message) => (
    <SubTitleWrapper>
      <Text weight={TEXT_WEIGHT.BOLD} size={TEXT_SIZE.T1} textColor={TEXT_COLOR.DARK_BLUE}>
        {this.props.t(message)}
      </Text>
    </SubTitleWrapper>
  );

  renderPracticeErrors = () => {
    const {
      activity: { practiceErrors },
    } = this.props;
    if (!practiceErrors) return null;
    const settings = {
      infinite: false,
      speed: 1000,
      slidesToShow: practiceErrors.length >= 2 ? 2 : 1,
      slidesToScroll: 1,
      centerPadding: 30,
      arrows: true,
    };
    return (
      <SliderView {...settings}>
        {practiceErrors.map((error) => (
          <SliderItem>
            <PracticeErrorsCard data={error} />
          </SliderItem>
        ))}
      </SliderView>
    );
  };

  renderSummary = () => {
    const { summary } = this.props.activity;
    return (
      <SectionWrapper>
        {this.renderSubTitle('UI.practiceOverview')}
        {summary.hasPassed ? (
          this.renderPracticeErrors()
        ) : (
          <Text className="noSummaryForIncomplete">{this.props.t('Informative.Negative.practiceOverview')}</Text>
        )}
      </SectionWrapper>
    );
  };

  renderPollDetails = () => {
    const { activity } = this.props;
    const schema = activity?.entity?.surveySchema;
    const surveyResponse = activity?.surveyResponse || {};

    if (activity?.pollSkipped) {
      return (
        <Text size={TEXT_SIZE.H2} textColor={TEXT_COLOR.DARK_BLUE} weight={TEXT_WEIGHT.BOLD}>
          {this.props.t('Poll.pollSkipped')}
        </Text>
      );
    }

    return (
      <SectionWrapper>
        {this.renderSubTitle('Common.UI.poll')}
        <SurveyJsPollRuntime schema={schema} data={surveyResponse} readOnly />
      </SectionWrapper>
    );
  };

  render() {
    const errorMsg = this.getErrorMessage();
    if (errorMsg) return this.renderError(errorMsg);

    const { loadingActivity, type, activity, isClinicalMentor } = this.props;
    const activityType = activity?.type;
    const trackable = type === ACTIVITY_TYPES.ACTIVITY_PRACTICE || type === ACTIVITY_TYPES.ACTIVITY_POLL;
    return loadingActivity ? (
      <SectionLoading className="pageLoading" />
    ) : (
      <Wrapper>
        {isClinicalMentor && trackable && activity?.id && (
          <TimeTracker
            key={activity.id}
            activityType={type === ACTIVITY_TYPES.ACTIVITY_POLL ? ActivityType.POLL : ActivityType.PRACTICE}
            activityId={activity.id}
            userId={activity.userId}
          />
        )}
        <MainSection>
          <BackButton
            onClick={this.onBackClicked}
            text={this.props.t('Actions.Navigation.goBack')}
            margin="0 0 8px -8px"
          />
          {this.renderHeader()}
          {activityType === ACTIVITY_TYPES.ACTIVITY_PRACTICE && this.renderSummary()}
          {activityType === ACTIVITY_TYPES.ACTIVITY_PRACTICE && this.renderExercises()}
          {activityType === ACTIVITY_TYPES.ACTIVITY_POLL && this.renderPollDetails()}
        </MainSection>
        {this.renderPosts()}
      </Wrapper>
    );
  }
}

ActivityPage.propTypes = {
  error: PropTypes.string,
  loadingActivity: PropTypes.bool,
  postingFeedback: PropTypes.bool,
  activity: PropTypes.shape({
    id: PropTypes.string,
    entity: PropTypes.shape({
      name: PropTypes.string,
    }),
    Threads: PropTypes.arrayOf(
      PropTypes.shape({
        Posts: PropTypes.arrayOf(
          PropTypes.shape({
            payload: PropTypes.object,
            createdAt: PropTypes.string,
          }),
        ),
      }),
    ),
    user: PropTypes.shape({
      fullName: PropTypes.string,
    }),
    summary: PropTypes.shape({
      achievedMasteryLevel: PropTypes.number,
      insights: PropTypes.array,
      hasPassed: PropTypes.bool,
    }),
    Exercises: PropTypes.array,
    ParentSkill: PropTypes.shape({
      name: PropTypes.string,
    }),
  }),
  loadedProductions: PropTypes.object,
  dispatchGetUserActivity: PropTypes.func,
  dispatchDismissActivity: PropTypes.func,
  dispatchPostFeedback: PropTypes.func,
  dispatchOpenPopup: PropTypes.func,
  dispatchGetExerciseProductions: PropTypes.func,
  type: PropTypes.string,
  isClinicalMentor: PropTypes.bool,

  // from router
  match: PropTypes.object,
  history: PropTypes.object,
};

function mapDispatchToProps(dispatch) {
  return {
    dispatchGetUserActivity: (activityType, activityId) => dispatch(getUserActivity(activityType, activityId)),
    dispatchOpenPopup: (popupType, options) => dispatch(openPopUp(popupType, options)),
    dispatchPostFeedback: (activity, text, dismiss, isMeaningful) => dispatch(postFeedback(activity, text, dismiss, isMeaningful)),
    dispatchDismissActivity: (activity, reason) => dispatch(postFeedback(activity, reason, true)),
    dispatchGetExerciseProductions: (id) => dispatch(getExerciseProductions(id)),
    dispatchDeleteFeedback: (id) => dispatch(deleteFeedback(id)),
  };
}

const mapStateToProps = (state) => {
  const activityPage = state.get('activityPage').toJS();
  const { roles = [] } = state.getIn(['app', 'authentication', 'user']).toJS();
  const isClinicalMentor = roles.some((roleId) => mentorRoles.includes(roleId));

  return { ...activityPage, isClinicalMentor };
};

const withReducer = injectReducer({ key: 'activityPage', reducer });
const withSaga = injectSaga({ key: 'activityPage', saga });
const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default compose(withReducer, withConnect, withRouter, withSaga, withTranslation())(ActivityPage);
